import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'

import {
  Panel,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  LINK_VARIANT_EXTRALIGHT,
  Toggle,
  TooltipUtils,
  TITLE_VARIANT_M,
  Title,
  SpacingVertical,
  SPACING_8,
  GridCell,
  Grid,
  ModalConfirm,
  GRID_VARIANT_VERTICAL_BIG,
  Flex,
  FlexItem,
  Text,
  HelpIcon,
  MessageBox
} from 'mailup-web-components'

import InputSenderEmail from './InputSenderEmail'
import InputSenderName from './InputSenderName'
import InputSenderReplyTo from './InputReplyTo'
import DynamicSenderEmail from './DynamicSenderEmail'
import DynamicSenderName from './DynamicSenderName'

import {
  isValidFormState,
  abTestState
} from 'Pages/AbTestWizard/recoil/state/abtest'
import { putBeListSenderInfoCallback } from 'Pages/AbTestWizard/recoil/callbacks/callbacks'
import { evalCondition } from 'mailup-react-helpers'

const BoxSenderConfig = () => {
  const intl = useIntl()

  const [{ step2 }, setAbTestState] = useRecoilState(abTestState)
  const isValidForm = useRecoilValue(isValidFormState)
  const [openModalSaveAsDefault, setOpenModalSaveAsDefault] = useState(false)
  const saveConfig = useRecoilCallback(putBeListSenderInfoCallback, [])

  const handleChange = () => {
    setAbTestState(prev => ({
      ...prev,
      step2: {
        ...prev.step2,
        step2IsDirty: true,
        senderInfo: {
          ...prev.step2.senderInfo,
          customSender: !prev.step2.senderInfo.customSender
        }
      }
    }))
  }

  const handleSave = () => {
    setOpenModalSaveAsDefault(true)
  }

  const closeModalSaveConfig = () => {
    setOpenModalSaveAsDefault(false)
  }

  const confirmModalSaveConfig = () => {
    saveConfig()
    setOpenModalSaveAsDefault(false)
  }

  return (
    <>
      <ModalConfirm
        show={openModalSaveAsDefault}
        title={intl.formatMessage({
          id: 'Load.EmailSendingWizard.btnSaveAsDefault'
        })}
        message={intl.formatMessage({
          id: 'Load.EmailCheckUp.TxtModalDefaulSender'
        })}
        onClickCancel={closeModalSaveConfig}
        stringCancel={intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        onClickConfirm={confirmModalSaveConfig}
        stringConfirm={intl.formatMessage({ id: 'Load.MailupCommon.Continue' })}
        showClose
      />
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.EmailSendingWizard.TitleSender' })}
      </Title>
      <SpacingVertical variant={SPACING_8} />
      <Panel collapsible={false}>
        <Grid cols={2}>
          <GridCell>
            <InputSenderEmail />
          </GridCell>
          <GridCell>
            <InputSenderName />
          </GridCell>
        </Grid>
        <Grid cols={2} variantVertical={GRID_VARIANT_VERTICAL_BIG}>
          {!step2.senderInfo.isPEC && (
            <>
              <GridCell span={2}>
                <SpacingVertical variant={SPACING_8} />
                <Flex alignItems='center'>
                  <Toggle
                    disabled={evalCondition('uo:EMAILSENDERLOCKED')}
                    checked={step2.senderInfo.customSender}
                    onChange={handleChange}
                    variant='sm'
                  />
                  <FlexItem margin='0 4px 0 8px'>
                    <Link onClick={handleChange}>
                      <Text>
                        {intl.formatMessage({
                          id: 'Load.EmailSendingWizard.ChangeSenderName'
                        })}
                      </Text>
                    </Link>
                  </FlexItem>

                  <FlexItem>
                    <TooltipUtils
                      id='customSenderTooltip'
                      type='light'
                      effect='solid'
                      className='c-tooltip'
                    >
                      {intl.formatMessage(
                        {
                          id:
                            'Load.EmailSendingWizard.DynamicallySelectSenderTooltipIntl'
                        },
                        {
                          linebreak: <br />,
                          strong: chunk => <strong>{chunk}</strong>
                        }
                      )}
                    </TooltipUtils>
                    <span data-tip data-for='customSenderTooltip'>
                      <HelpIcon />
                    </span>
                  </FlexItem>
                </Flex>
              </GridCell>

              {step2.senderInfo.customSender && (
                <>
                  <GridCell span={2}>
                    <MessageBox variant='info'>
                      {intl.formatMessage({
                        id: 'Load.EmailSendingWizard.DynamicSenderInfo'
                      })}
                    </MessageBox>
                  </GridCell>
                  <GridCell>
                    <DynamicSenderEmail />
                  </GridCell>

                  <GridCell>
                    <DynamicSenderName />
                  </GridCell>
                </>
              )}
            </>
          )}

          <GridCell>
            <InputSenderReplyTo />
          </GridCell>

          <GridCell>
            <Flex justifyContent='flex-end' alignItems='flex-end' height='100%'>
              <Link
                data-mu-test='sending-wizard-save-sender-config'
                variant={
                  isValidForm ? LINK_VARIANT_HIGHLIGHT : LINK_VARIANT_EXTRALIGHT
                }
                onClick={handleSave}
                disabled={!isValidForm}
              >
                {intl.formatMessage({
                  id: 'Load.EmailSendingWizard.btnSaveAsDefault'
                })}
              </Link>
            </Flex>
          </GridCell>
        </Grid>
      </Panel>
      <SpacingVertical />
    </>
  )
}

export default BoxSenderConfig
